/**
 * The one place the console reads its build-time environment. Vite inlines
 * `import.meta.env` at build time, so these are fixed per build, not per visit.
 *
 * There is deliberately no token here. Which service to talk to, and with what token, is
 * the visitor's choice at runtime (see `connection.ts`): the console is hosted as a static
 * site, and anything baked into a public bundle is published.
 */
export interface WebConfig {
  /**
   * The service URL the bundle was built with, if any — a *default*, not the source of
   * truth: a `?backend=` link, and then this browser's stored choice, both win over it.
   * Its absence is what makes the setup screen appear on a first visit.
   */
  readonly defaultApiUrl?: string;
  /** Offered in the setup screen's field: where `npm start` serves the API. */
  readonly suggestedApiUrl: string;
  /** A chain walk can take several Alpaca pages; a backtest start prices both legs first. */
  readonly requestTimeoutMs: number;
  /** How often a running backtest is polled. */
  readonly backtestPollMs: number;
}

function readUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return undefined;
  }
  return value.trim().replace(/\/+$/, '');
}

export const config: WebConfig = {
  // No fallback: a bundle built without VITE_FLEECE_API_URL used to assume loopback, which
  // is right on the machine running the service and wrong for the hosted copy. Unset
  // means "ask", with the loopback address offered in the field instead.
  defaultApiUrl: readUrl(import.meta.env.VITE_FLEECE_API_URL),
  suggestedApiUrl: 'http://127.0.0.1:3100',
  requestTimeoutMs: 30_000,
  backtestPollMs: 1_000,
};
